import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;
const REFRESH_URL = API_URL + "source/refresh.php";
const LOGOUT_URL = API_URL + "source/logout.php";

let inflight = null;

/**
 * Single-flight refresh: concurrent 401s share one /refresh call.
 * Resolves to the new access token and updates sessionStorage.
 */
export function refreshAccessToken() {
  if (inflight) return inflight;
  inflight = axios
    .post(REFRESH_URL, null, { withCredentials: true })
    .then((r) => {
      if (!r || !r.data || r.data.success !== 1 || !r.data.token) {
        throw new Error("refresh_failed");
      }
      sessionStorage.setItem("token", r.data.token);
      if (r.data.csrf_token) {
        sessionStorage.setItem("csrf_token", r.data.csrf_token);
      }
      return r.data.token;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function isRefreshUrl(url) {
  if (!url) return false;
  return url.indexOf("source/refresh.php") !== -1;
}

export function forceLogout() {
  try {
    sessionStorage.clear();
  } catch (_) {}
  const base = process.env.REACT_APP_BASE_NAME || "";
  if (typeof window !== "undefined" && window.location) {
    window.location.href = base + "/login";
  }
}

/**
 * Server-side logout: revokes refresh-token family + clears the cookie.
 * Best-effort; never throws — caller should still clear sessionStorage.
 */
export function serverLogout() {
  return axios
    .post(LOGOUT_URL, null, { withCredentials: true })
    .catch(() => null);
}

/**
 * Attach a 401 response interceptor that:
 *  - skips refresh.php itself
 *  - calls refreshAccessToken() (single-flight)
 *  - replays the original request once with the new bearer
 *  - on refresh failure, forces logout
 */
export function attachRefreshInterceptor(instance) {
  instance.interceptors.response.use(
    (r) => r,
    async (err) => {
      const cfg = err && err.config ? err.config : null;
      const status = err && err.response ? err.response.status : null;
      if (!cfg || status !== 401) return Promise.reject(err);
      if (cfg._retry || isRefreshUrl(cfg.url)) {
        if (isRefreshUrl(cfg.url)) forceLogout();
        return Promise.reject(err);
      }
      cfg._retry = true;
      try {
        const newToken = await refreshAccessToken();
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = "Bearer " + newToken;
        const csrf = sessionStorage.getItem("csrf_token");
        if (csrf) cfg.headers["X-CSRF-Token"] = csrf;
        return instance(cfg);
      } catch (_) {
        forceLogout();
        return Promise.reject(err);
      }
    }
  );
}
